/**
 * Seats, sides and who controls them.
 *
 * Bridge has four seats, played in partnerships: North/South against East/West. The order below
 * is clockwise, which is the order of play. Nothing in the engine assumes which seat a human
 * sits in: every seat has a CONTROLLER, human or AI, so the same rules serve a single player
 * (one human and three AI), a mixed table (two to four humans with AI filling the rest), and an
 * all-AI table (robot self-play, which the contract table's calibration needs).
 */
export type BridgeSeat = 'N' | 'E' | 'S' | 'W';
export type BridgeSide = 'NS' | 'EW';
export type BridgeController = 'human' | 'ai';
export type BridgeControllers = Readonly<Record<BridgeSeat, BridgeController>>;

/** Clockwise, which is the order of play. */
export const BRIDGE_SEATS: readonly BridgeSeat[] = ['N', 'E', 'S', 'W'];

/** One human in the South seat with an AI partner and two AI opponents. */
export const SINGLE_PLAYER: BridgeControllers = { N: 'ai', E: 'ai', S: 'human', W: 'ai' };
/** Four AI seats: robot self-play and calibration. */
export const ALL_AI: BridgeControllers = { N: 'ai', E: 'ai', S: 'ai', W: 'ai' };

const index = (seat: BridgeSeat): number => BRIDGE_SEATS.indexOf(seat);

export const sideOf = (seat: BridgeSeat): BridgeSide => (seat === 'N' || seat === 'S' ? 'NS' : 'EW');
export const oppositeSide = (side: BridgeSide): BridgeSide => (side === 'NS' ? 'EW' : 'NS');
export const seatsOf = (side: BridgeSide): readonly [BridgeSeat, BridgeSeat] =>
  side === 'NS' ? ['N', 'S'] : ['E', 'W'];
export const partnerOf = (seat: BridgeSeat): BridgeSeat => BRIDGE_SEATS[(index(seat) + 2) % 4] as BridgeSeat;
/** The next seat clockwise: the one that plays after `seat`. */
export const leftOf = (seat: BridgeSeat): BridgeSeat => BRIDGE_SEATS[(index(seat) + 1) % 4] as BridgeSeat;

/** The four seats in clockwise order STARTING after `seat` (so `seat` itself comes last). */
export function clockwiseAfter(seat: BridgeSeat): BridgeSeat[] {
  return [1, 2, 3, 4].map((n) => BRIDGE_SEATS[(index(seat) + n) % 4] as BridgeSeat);
}

export const humanSeats = (controllers: BridgeControllers): BridgeSeat[] =>
  BRIDGE_SEATS.filter((s) => controllers[s] === 'human');
